import axios from 'axios';

import createApi from '../../../app/createApi';
import cancelUploadGql from './cancelUpload.graphql';
import commitUploadGql from './commitUpload.graphql';
import newUploadGql from './newUpload.graphql';

const storageTypes = {
  Aws: 'aws',
  App: 'local',
};

const cancelUploadFnMap = new Map();
const pendingCancelUploadTokens = new Set();

const clearCancelUpload = uploadToken => {
  cancelUploadFnMap.delete(uploadToken);
  pendingCancelUploadTokens.delete(uploadToken);
};

const registerCancelUpload = (uploadToken, cancelFn) => {
  cancelUploadFnMap.set(uploadToken, cancelFn);

  if (pendingCancelUploadTokens.has(uploadToken)) {
    pendingCancelUploadTokens.delete(uploadToken);
    cancelFn();
  }
};

const secureIdHexRegex = /^[a-fA-F0-9]+$/;

const normalizeSecureId = value => {
  if (value === undefined || value === null) {
    return value;
  }

  const stringValue = String(value);
  const isValidHex = secureIdHexRegex.test(stringValue);
  const isCipherBlockHexLength =
    // eslint-disable-next-line no-magic-numbers
    stringValue.length >= 32 && stringValue.length % 32 === 0;

  return isValidHex && isCipherBlockHexLength ? stringValue : undefined;
};

export default function fileStorageService(apiUrl, apolloClient) {
  const api = createApi({ apiUrl, log: console });
  const defaultOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const parseResponse = ({ data }) => {
    if (data.ok) {
      return { data };
    }

    const error = new Error(data.error);
    error.maxFileSize = data.maxFileSize;
    error.canUploadSize = data.canUploadSize;

    throw error;
  };

  const getUploadUrl = async (
    file,
    fileCategory,
    { organisationGroupId, tenantId },
  ) => {
    const { fileId, fileName, mimeType, fileSize } = file;
    const normalizedFileId = normalizeSecureId(fileId);
    const params = {
      fileName,
      mimeType,
      fileSize,
      fileCategory,
      organisationGroupId,
      tenantId,
    };

    if (normalizedFileId) {
      params.fileId = normalizedFileId;
    }

    if (!fileSize) {
      return {
        ok: false,
        error: 'Empty file is not allowed',
      };
    }

    const {
      data: { newUpload },
    } = await apolloClient.mutate({
      mutation: newUploadGql,
      variables: {
        params,
      },
    });

    return newUpload;
  };

  const uploadFile = async (
    { data },
    { storageType, uploadToken, uploadConfig, ok, error },
    progressCallback,
  ) => {
    if (!ok && error) {
      throw new Error(error);
    }

    switch (storageType) {
      case storageTypes.Aws: {
        await uploadToAws(
          uploadToken,
          uploadConfig,
          data,
          progress => progressCallback && progressCallback(progress),
        );
        break;
      }
      default: {
        await uploadToApp(
          uploadToken,
          uploadConfig.uploadUrl,
          data,
          progress => progressCallback && progressCallback(progress),
        );
      }
    }
  };

  const uploadToAws = async (
    uploadToken,
    uploadConfig,
    file,
    progressCallback,
  ) => {
    // small performance impact on first function call
    const { crypto } = await import('aws-sdk/lib/util');
    const Evaporate = await import('evaporate');
    {
      // Evaporate does not work without S3
      await import('aws-sdk/clients/s3');
    }

    const md5 = crypto.md5;
    const sha256 = crypto.sha256;

    const {
      signUrl,
      accessKey,
      bucketName,
      region,
      filePath,
      sessionToken,
    } = uploadConfig;
    const evaporate = await Evaporate.default.create({
      signerUrl: signUrl,
      aws_url: `https://${bucketName}.s3.${region}.amazonaws.com`,
      aws_key: accessKey,
      bucket: bucketName,
      awsRegion: region,
      cloudfront: true,
      computeContentMd5: true,
      mockLocalStorage: true,
      cryptoMd5Method(data) {
        return md5(data, 'base64');
      },
      cryptoHexEncodedHash256(data) {
        return sha256(data, 'hex');
      },
      logging: false,
      s3FileCacheHoursAgo: 1,
      allowS3ExistenceOptimization: false,
      customAuthMethod: undefined,
    });

    let isCancelled = false;
    registerCancelUpload(uploadToken, async () => {
      if (isCancelled) return;
      isCancelled = true;
      clearCancelUpload(uploadToken);
      await evaporate.cancel(`${bucketName}/${filePath}`);
    });

    try {
      const options = {
        name: filePath,
        file,
        progress(p) {
          if (progressCallback) {
            progressCallback(p);
          }
        },
      };

      if (sessionToken) {
        options.xAmzHeadersCommon = { 'x-amz-security-token': sessionToken };
        options.xAmzHeadersAtInitiate = {
          'x-amz-security-token': sessionToken,
        };
      }

      await evaporate.add(options, { bucket: bucketName, aws_key: accessKey });
    } catch (e) {
      if (!isCancelled) {
        throw e;
      }
    } finally {
      clearCancelUpload(uploadToken);
    }
  };

  const uploadToApp = async (
    uploadToken,
    uploadUrl,
    file,
    progressCallback,
  ) => {
    const cancelTokenSource = axios.CancelToken.source();

    let isCancelled = false;
    registerCancelUpload(uploadToken, () => {
      if (isCancelled) return;
      isCancelled = true;
      clearCancelUpload(uploadToken);
      cancelTokenSource.cancel('Operation cancelled by user');
    });

    try {
      const formData = new FormData();
      formData.set('file', file);

      const response = await api.request(uploadUrl, {
        ...defaultOptions,
        data: formData,
        withBaseUrl: false,
        onUploadProgress(progressEvent) {
          if (progressCallback) {
            progressCallback(progressEvent.loaded / progressEvent.total);
          }
        },
        cancelToken: cancelTokenSource.token,
      });

      return parseResponse(response);
    } catch (e) {
      if (!isCancelled) {
        throw e;
      }
    } finally {
      clearCancelUpload(uploadToken);
    }
  };

  const commitUpload = async uploadToken => {
    const {
      data: { commitUpload },
    } = await apolloClient.mutate({
      mutation: commitUploadGql,
      variables: { uploadToken },
    });

    return commitUpload;
  };

  const cancelUpload = async uploadToken => {
    if (uploadToken && cancelUploadFnMap.has(uploadToken)) {
      cancelUploadFnMap.get(uploadToken)();
    } else if (uploadToken) {
      pendingCancelUploadTokens.add(uploadToken);
    }

    const {
      data: { cancelUpload },
    } = await apolloClient.mutate({
      mutation: cancelUploadGql,
      variables: { uploadToken },
    });

    return cancelUpload;
  };

  const getFileUrl = ({
    fileId,
    version,
    tenantId,
    organisationGroupId,
    width,
    height,
  }) => {
    const queryParam = [`tenantId=${tenantId}`];
    if (organisationGroupId) {
      queryParam.push(`organisationGroupId=${organisationGroupId}`);
    }
    if (width) {
      queryParam.push(`width=${width}`);
    }
    if (height) {
      queryParam.push(`height=${height}`);
    }

    const queryString = `?${queryParam.join('&')}`;

    if (version) {
      return `${apiUrl}/file/${fileId}/${version}/download${queryString}`;
    }

    return `${apiUrl}/file/${fileId}/download${queryString}`;
  };

  const getFileId = src => {
    if (src && fileIdRegex.test(src)) {
      return src.replaceAll(fileIdRegex, '$1');
    }

    return undefined;
  };

  return {
    getUploadUrl,
    uploadFile,
    cancelUpload,
    commitUpload,
    getFileUrl,
    getFileId,
  };
}

const fileIdRegex = new RegExp('.*/file/(.*?)/.*', 'g');
